import { useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "react-query"
import { useTranslation } from "react-i18next"
import { Tooltip } from "antd"
import { InformationCircleIcon } from "@heroicons/react/outline"

import { Button } from "../../../../components"
import { fetchShippingCoveragesList } from "../../../../services/shippingMethodsServices"
import { fetchShippingCostsForMethod } from "../../../../services"
import { SectionTitle, FieldLabel } from "./formPrimitives"
import { useCreateCoveragesDraft, DRAFT_STATUS } from "./useCreateCoveragesDraft"
import { BindXdockPanel } from "./BindXdockPanel"
import { ConfigureShippingCostPanel } from "./ConfigureShippingCostPanel"
import { CoverageJobProgressPanel } from "../CoverageJobProgressPanel"
import { MAX_RANGE, computeRange, expandRangeValues } from "./zipRange"

// Reuse the existing draft-flow translations for field labels / errors, and the
// coverage-manager modal namespace for the form-specific copy.
const C = "coberturas.createCoverages"
const M = "lastmile_management.coverage_manager.create_modal"

// Shared input look — matches the update modal (rounded, gray border, sm text).
const INPUT_CLS =
  "block w-full rounded-md border-gray-300 shadow-sm placeholder-gray-400 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"

const INITIAL = {
  value: "",
  start: "",
  end: "",
  zone: "",
  status: "active", // active | disabled
  cost: "", // per-zone shipping cost (optional)
  extended_zone: false,
  po_box_dropoff: false,
  delivery_sla_in_hours: "",
  delivery_sla_offset: "",
  eta_offset: "",
  content_value_fee: "",
  fee_offset: "",
}

const INT_FIELDS = ["delivery_sla_in_hours", "delivery_sla_offset", "eta_offset"]
const FLOAT_FIELDS = ["content_value_fee", "fee_offset"]
const CURRENCY_BY_COUNTRY = { MX: "MXN", BR: "BRL", CO: "COP", CL: "CLP" }

const stripEmpty = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== "" && v != null && !Number.isNaN(v))
  )

// Attributes shared by every coverage in the batch (everything except the postal
// code itself): zone, flags and the numeric time/fee fields, coerced to numbers.
const buildShared = (row) => {
  const out = {
    zone: row.zone.trim(),
    enabled: row.status === "active",
    extended_zone: row.extended_zone,
    po_box_dropoff: row.po_box_dropoff,
  }
  INT_FIELDS.forEach((k) => {
    if (row[k] !== "" && row[k] != null) out[k] = parseInt(row[k], 10)
  })
  FLOAT_FIELDS.forEach((k) => {
    if (row[k] !== "" && row[k] != null) out[k] = parseFloat(row[k])
  })
  return out
}

// Single-CP payload: one coverage from the form's `value`.
const buildCoverage = (row) => stripEmpty({ value: row.value.trim(), ...buildShared(row) })

// Range payload: one coverage per postal code in the expanded range, all sharing
// the same zone/flags/time/fee attributes.
const buildRangeCoverages = (row, range) => {
  const shared = buildShared(row)
  return expandRangeValues(range).map((value) => stripEmpty({ value, ...shared }))
}

// Short description shown under a field.
const Helper = ({ children }) => <div className="mt-1 text-xs text-gray-400">{children}</div>

// Field label with an info tooltip next to it.
const LabelTip = ({ children, tip, required }) => (
  <div className="mb-1 flex items-center gap-1">
    <FieldLabel required={required}>{children}</FieldLabel>
    <Tooltip title={tip}>
      <InformationCircleIcon className="-mt-1 h-4 w-4 cursor-help text-gray-400" />
    </Tooltip>
  </div>
)

// Toggle switch (replaces plain checkboxes for boolean flags).
const Toggle = ({ checked, onChange, children, tip }) => (
  <div className="flex items-center gap-2">
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 ${
        checked ? "bg-indigo-600" : "bg-gray-300"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </button>
    <span className="text-sm text-gray-700">{children}</span>
    {tip && (
      <Tooltip title={tip}>
        <InformationCircleIcon className="h-4 w-4 cursor-help text-gray-400" />
      </Tooltip>
    )}
  </div>
)

// Input with an in-box suffix (e.g. "horas", "%"), matching the update modal.
const SuffixInput = ({ value, onChange, placeholder, suffix, sanitize, disabled }) => (
  <div className="relative">
    <input
      type="text"
      inputMode={sanitize === "decimal" ? "decimal" : "numeric"}
      value={value}
      disabled={disabled}
      onChange={(e) =>
        onChange(
          sanitize === "decimal"
            ? e.target.value.replace(/[^0-9.]/g, "")
            : e.target.value.replace(/[^0-9]/g, "")
        )
      }
      placeholder={placeholder}
      className={`${INPUT_CLS} ${suffix ? "pr-14" : ""} ${disabled ? "bg-gray-50 text-gray-500" : ""}`}
    />
    {suffix && (
      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 sm:text-sm">
        {suffix}
      </span>
    )}
  </div>
)

const IntInput = (props) => <SuffixInput {...props} sanitize="int" />
const DecimalInput = (props) => <SuffixInput {...props} sanitize="decimal" />

// Small red pill used to flag a row error. Clickable variants open a remediation drawer.
const ErrorPill = ({ children, onClick }) => {
  const base =
    "inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-200"
  if (!onClick) return <span className={base}>{children}</span>
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} cursor-pointer transition-colors hover:bg-red-100`}
    >
      {children}
    </button>
  )
}

/**
 * Single-CP creation form rendered inside the "Crear cobertura" modal.
 *
 * It reuses the bulk-create draft machinery (validate → commit → job progress)
 * with a single coverage, plus the shared remediation drawers for zones that
 * still need an xdock binding or a shipping cost. All fields are shown at once,
 * styled to match the update modal (rounded native inputs).
 */
export const SingleCoverageForm = ({ shippingMethod, onBack, onClose, onCreated }) => {
  const { t } = useTranslation()
  const [mode, setMode] = useState("single") // "single" | "range"
  const [formRow, setFormRow] = useState(INITIAL)
  const [openPanel, setOpenPanel] = useState(null) // { type: "bindXdock" | "configureCost", zone }
  const [progressJobId, setProgressJobId] = useState(null)
  const [zonesExpanded, setZonesExpanded] = useState(false)

  const draft = useCreateCoveragesDraft({ shippingMethodId: shippingMethod?.id })
  const country = shippingMethod?.country

  const range = useMemo(
    () => computeRange({ start: formRow.start, end: formRow.end }),
    [formRow.start, formRow.end]
  )

  // Zone suggestions = distinct zones already used by this method's coverages
  // (the xdocks endpoint doesn't return zone names).
  const { data: existingCoverages } = useQuery(
    ["shipping_coverages_zones", shippingMethod?.id],
    () => fetchShippingCoveragesList({ shipping_method_id: shippingMethod.id, per_page: 200 }),
    { enabled: Boolean(shippingMethod?.id), staleTime: 60_000 }
  )
  const availableZones = useMemo(
    () =>
      Array.from(
        new Set((existingCoverages?.coverages || []).map((c) => c.zone).filter(Boolean))
      ).sort(),
    [existingCoverages]
  )

  // Shipping costs already configured for this method, indexed by zone, so we can
  // show the price set for the zone the operator types.
  const { data: shippingCostsData } = useQuery(
    ["shipping_costs", shippingMethod?.id],
    () => fetchShippingCostsForMethod(shippingMethod.id),
    { enabled: Boolean(shippingMethod?.id), staleTime: 60_000 }
  )
  const costByZone = useMemo(() => {
    const map = {}
    ;(shippingCostsData?.shipping_costs || []).forEach((c) => {
      map[c.tarif_zone] = c
    })
    return map
  }, [shippingCostsData])

  const existingZoneCost = costByZone[formRow.zone.trim()] || null

  const currency = CURRENCY_BY_COUNTRY[country] || ""
  const lastCostZoneRef = useRef(null)

  // When the operator types/selects a zone that already has a configured cost,
  // show that price in the cost field (read-only — it isn't re-sent). Clear it
  // when moving to a zone without a configured cost so the old price isn't reused.
  useEffect(() => {
    setField("cost", existingZoneCost ? String(existingZoneCost.cost) : "")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingZoneCost?.id])

  // Keep the draft's coverages in sync with the form. In "range" mode a valid
  // range expands to one coverage per CP; otherwise it's a single coverage.
  // Editing any field invalidates the previous validation (setCoverages resets
  // status/response), so stale row errors clear as soon as something changes.
  // Also sync the (optional) per-zone shipping cost as a prerequisite, keeping a
  // single entry for the current zone.
  useEffect(() => {
    if (mode === "range") {
      draft.setCoverages(range.valid ? buildRangeCoverages(formRow, range) : [])
    } else {
      draft.setCoverages([buildCoverage(formRow)])
    }

    const zone = formRow.zone.trim()
    const costNum = parseFloat(formRow.cost)
    const hasCost = String(formRow.cost).trim() !== "" && !Number.isNaN(costNum)
    if (lastCostZoneRef.current && lastCostZoneRef.current !== zone) {
      draft.removeShippingCost(lastCostZoneRef.current)
      lastCostZoneRef.current = null
    }
    // Only send a NEW cost for zones that don't already have one (avoids the
    // "shipping cost already exists" error for zones with a configured price).
    if (hasCost && zone && !costByZone[zone]) {
      draft.appendShippingCost({ tarif_zone: zone, cost: costNum, currency_code: currency || undefined })
      lastCostZoneRef.current = zone
    } else if (lastCostZoneRef.current) {
      draft.removeShippingCost(lastCostZoneRef.current)
      lastCostZoneRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formRow, mode])

  // SLA is required; the rest of "Tiempos y costos" stays optional.
  const slaFilled = String(formRow.delivery_sla_in_hours).trim() !== ""
  const canSubmit =
    mode === "range"
      ? Boolean(range.valid && formRow.zone.trim() && slaFilled)
      : Boolean(formRow.value.trim() && formRow.zone.trim() && slaFilled)
  const busy =
    draft.status === DRAFT_STATUS.VALIDATING || draft.status === DRAFT_STATUS.COMMITTING

  const setField = (k, v) => setFormRow((prev) => ({ ...prev, [k]: v }))

  const handleSubmit = async () => {
    // Dry-run validation first; only commit when the server is happy.
    const data = await draft.validate()
    if (!data) return // network error → surfaced via draft.response
    if (data.error_code) return // validation failed → row errors render below
    const committed = await draft.commit()
    if (committed?.job_id) setProgressJobId(committed.job_id)
  }

  const zone = formRow.zone.trim()

  // Aggregate errors across every row (1 for single, N for a range): zone-level
  // problems apply to the whole batch so they show once; per-CP problems (e.g.
  // already-exists) collapse into a count instead of one chip per postal code.
  const aggregatedErrors = useMemo(() => {
    const rows = draft.response?.rows
    if (!rows || !rows.length) return []
    const byCode = {}
    rows.forEach((r) => {
      ;(r.errors || []).forEach((e) => {
        if (!byCode[e.code]) byCode[e.code] = { code: e.code, count: 0, sample: e }
        byCode[e.code].count += 1
      })
    })
    return Object.values(byCode)
  }, [draft.response])

  const renderErrorChip = ({ code, count, sample }, i) => {
    if (code === "ZONE_HAS_NO_XDOCK") {
      return (
        <ErrorPill key={i} onClick={() => setOpenPanel({ type: "bindXdock", zone })}>
          {t(`${C}.errors.ZONE_HAS_NO_XDOCK`)} · {t(`${C}.preview.fix`)}
        </ErrorPill>
      )
    }
    if (code === "ZONE_HAS_NO_SHIPPING_COST") {
      return (
        <ErrorPill key={i} onClick={() => setOpenPanel({ type: "configureCost", zone })}>
          {t(`${C}.errors.ZONE_HAS_NO_SHIPPING_COST`)} · {t(`${C}.preview.fix`)}
        </ErrorPill>
      )
    }
    if (code === "COVERAGE_ALREADY_EXISTS") {
      return (
        <ErrorPill key={i}>
          {count > 1
            ? t(`${M}.single.already_exists_count`, { count })
            : t(`${C}.errors.COVERAGE_ALREADY_EXISTS`, { id: sample.existing_coverage_id })}
        </ErrorPill>
      )
    }
    const label = t(`${C}.errors.${code}`, code)
    return <ErrorPill key={i}>{count > 1 ? `${label} (${count})` : label}</ErrorPill>
  }

  const networkError =
    draft.status === DRAFT_STATUS.ERROR && draft.response?.error_code === "NETWORK_ERROR"

  // Job running / finished → show progress inline (replacing the form), so we
  // don't stack a second dialog on top of this modal.
  if (progressJobId) {
    return (
      <CoverageJobProgressPanel
        jobId={progressJobId}
        onClose={(finalStatus) => {
          setProgressJobId(null)
          if (finalStatus && finalStatus !== "failed") {
            onCreated?.()
            onClose?.()
          }
        }}
        onCompleted={() => onCreated?.()}
      />
    )
  }

  // Shared identity fields reused by both single and range layouts.
  const statusField = (
    <label className="block">
      <FieldLabel required>{t(`${C}.fields.status`)}</FieldLabel>
      <select
        value={formRow.status}
        onChange={(e) => setField("status", e.target.value)}
        className={INPUT_CLS}
      >
        <option value="active">{t(`${C}.status_active`)}</option>
        <option value="disabled">{t(`${C}.status_disabled`)}</option>
      </select>
    </label>
  )
  const zoneField = (
    <label className="block">
      <FieldLabel required>{t(`${C}.fields.zone`)}</FieldLabel>
      <input
        type="text"
        value={formRow.zone}
        onChange={(e) => setField("zone", e.target.value)}
        placeholder="Zona A"
        className={INPUT_CLS}
      />
    </label>
  )
  const costField = (
    <label className="block">
      <LabelTip tip={t(`${C}.fieldTip.cost`)}>{t(`${C}.fields.cost`)}</LabelTip>
      <DecimalInput
        value={formRow.cost}
        onChange={(v) => setField("cost", v)}
        placeholder="0.00"
        suffix={currency}
        disabled={Boolean(existingZoneCost)}
      />
      {existingZoneCost && <Helper>{t(`${C}.fieldHelp.cost_existing`)}</Helper>}
    </label>
  )

  return (
    <div className="space-y-5">
      {/* Modo: un solo CP vs. rango de CP (en Brasil se crean por rango). */}
      <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
        {["single", "range"].map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              mode === m ? "bg-white text-indigo-700 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {t(`${M}.single.tab_${m}`)}
          </button>
        ))}
      </div>

      {/* Identificación: código(s) postal(es), estado, zona, costo y banderas */}
      <section className="space-y-4">
        <SectionTitle>{t(`${M}.single.section_location`)}</SectionTitle>

        {mode === "range" ? (
          <>
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <FieldLabel required>{t(`${C}.fields.start`)}</FieldLabel>
                <input
                  type="text"
                  value={formRow.start}
                  onChange={(e) => setField("start", e.target.value)}
                  placeholder="01000"
                  className={INPUT_CLS}
                />
              </label>
              <label className="block">
                <FieldLabel required>{t(`${C}.fields.end`)}</FieldLabel>
                <input
                  type="text"
                  value={formRow.end}
                  onChange={(e) => setField("end", e.target.value)}
                  placeholder="01999"
                  className={INPUT_CLS}
                />
              </label>
            </div>

            <div className="min-h-5 text-sm">
              {range.valid && (
                <span className="text-gray-600">
                  {t(`${C}.range.preview`, { count: range.count })}
                </span>
              )}
              {!range.valid && range.reason && formRow.start && formRow.end && (
                <span className="text-red-600">
                  {t(`${C}.range.error.${range.reason}`, { max: MAX_RANGE, count: range.count })}
                </span>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4">
              {statusField}
              {zoneField}
              {costField}
            </div>
          </>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <FieldLabel required>{t(`${C}.fields.value`)}</FieldLabel>
              <input
                type="text"
                value={formRow.value}
                onChange={(e) => setField("value", e.target.value)}
                placeholder="01000"
                className={INPUT_CLS}
              />
            </label>
            {statusField}
            {zoneField}
            {costField}
          </div>
        )}

        {availableZones.length > 0 && (
          <div className="-mt-1 flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-gray-400">{t(`${C}.fields.zoneSuggestions`)}:</span>
            {(zonesExpanded ? availableZones : availableZones.slice(0, 3)).map((z) => {
              const active = formRow.zone.trim() === z
              return (
                <button
                  key={z}
                  type="button"
                  onClick={() => setField("zone", z)}
                  className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                    active
                      ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                      : "border-gray-200 bg-white text-gray-600 hover:border-indigo-300 hover:text-indigo-600"
                  }`}
                >
                  {z}
                </button>
              )
            })}
            {availableZones.length > 3 && (
              <button
                type="button"
                onClick={() => setZonesExpanded((v) => !v)}
                className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
              >
                {zonesExpanded ? t(`${C}.see_less`) : t(`${C}.see_more`)}
              </button>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-gray-100 pt-4">
          <Toggle
            checked={formRow.extended_zone}
            onChange={(v) => setField("extended_zone", v)}
          >
            {t(`${C}.fields.extended_zone`)}
          </Toggle>
          <Toggle
            checked={formRow.po_box_dropoff}
            onChange={(v) => setField("po_box_dropoff", v)}
            tip={t(`${C}.fieldTip.po_box_dropoff`)}
          >
            {t(`${C}.fields.po_box_dropoff`)}
          </Toggle>
        </div>
      </section>

      {/* Tiempos y costos */}
      <section className="space-y-3 border-t border-gray-100 pt-5">
        <SectionTitle>{t(`${M}.single.section_delivery`)}</SectionTitle>

        <div className="grid grid-cols-2 gap-x-4 gap-y-4 lg:grid-cols-3">
          <label className="block">
            <FieldLabel required>{t(`${C}.fields.delivery_sla_in_hours`)}</FieldLabel>
            <IntInput
              value={formRow.delivery_sla_in_hours}
              onChange={(v) => setField("delivery_sla_in_hours", v)}
              placeholder="24"
              suffix={t(`${C}.units.hours`)}
            />
            <Helper>{t(`${C}.fieldHelp.delivery_sla_in_hours`)}</Helper>
          </label>

          <label className="block">
            <FieldLabel>{t(`${C}.fields.delivery_sla_offset`)}</FieldLabel>
            <IntInput
              value={formRow.delivery_sla_offset}
              onChange={(v) => setField("delivery_sla_offset", v)}
              placeholder="0"
              suffix={t(`${C}.units.hours`)}
            />
            <Helper>{t(`${C}.fieldHelp.delivery_sla_offset`)}</Helper>
          </label>

          <label className="block">
            <FieldLabel>{t(`${C}.fields.eta_offset`)}</FieldLabel>
            <IntInput
              value={formRow.eta_offset}
              onChange={(v) => setField("eta_offset", v)}
              placeholder="0"
              suffix={t(`${C}.units.hours`)}
            />
            <Helper>{t(`${C}.fieldHelp.eta_offset`)}</Helper>
          </label>

          <label className="block">
            <LabelTip tip={t(`${C}.fieldTip.content_value_fee`)}>
              {t(`${C}.fields.content_value_fee`)}
            </LabelTip>
            <DecimalInput
              value={formRow.content_value_fee}
              onChange={(v) => setField("content_value_fee", v)}
              placeholder="0.00"
              suffix={t(`${C}.units.percent`)}
            />
            <Helper>{t(`${C}.fieldHelp.content_value_fee`)}</Helper>
          </label>

          <label className="block">
            <FieldLabel>{t(`${C}.fields.fee_offset`)}</FieldLabel>
            <DecimalInput
              value={formRow.fee_offset}
              onChange={(v) => setField("fee_offset", v)}
              placeholder="0"
            />
            <Helper>{t(`${C}.fieldHelp.fee_offset`)}</Helper>
          </label>
        </div>
      </section>

      {aggregatedErrors.length > 0 && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3">
          <p className="mb-2 text-sm font-medium text-red-800">{t(`${M}.single.fix_errors`)}</p>
          <div className="flex flex-wrap gap-1.5">{aggregatedErrors.map(renderErrorChip)}</div>
        </div>
      )}

      {networkError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {t(`${M}.single.error`)}
        </div>
      )}

      <div className="flex gap-3 pt-1">
        <Button type="secondary" className="flex-1" onClick={onBack}>
          {t("common.cancel")}
        </Button>
        <Button
          type="primary"
          className="flex-1"
          disabled={!canSubmit || busy}
          loading={busy}
          loading_label={t(`${C}.create`) + "…"}
          onClick={handleSubmit}
        >
          {t(`${M}.single.submit`)}
        </Button>
      </div>

      <BindXdockPanel
        open={openPanel?.type === "bindXdock"}
        zone={openPanel?.zone}
        country={country}
        remediation={draft.response?.remediation}
        draftBindings={draft.xdockBindings}
        onAppend={(xdockId, zoneNames) => {
          draft.appendXdockBinding(xdockId, zoneNames)
          setOpenPanel(null)
        }}
        onClose={() => setOpenPanel(null)}
      />

      <ConfigureShippingCostPanel
        open={openPanel?.type === "configureCost"}
        tarifZone={openPanel?.zone}
        country={country}
        remediation={draft.response?.remediation}
        draftCosts={draft.shippingCosts}
        onAppend={(cost) => {
          draft.appendShippingCost(cost)
          setOpenPanel(null)
        }}
        onClose={() => setOpenPanel(null)}
      />
    </div>
  )
}
